/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { otpTokenStore } from '../../utils/otpTokenStore';
import {
  useAnonymousSsoCredentials,
  useSsoCredentials,
  type SsoCredentialsWithOptionalUser,
} from '../useSsoCredentials';
import { RECAPTCHA_ACTIONS } from '../../constants/recaptcha';
import { TWO_FA_LOGIN_METHOD } from '../../constants/twoFa';
import {
  AccessTokenResponse,
  ChangePasswordRequestBody,
  GenerateOtpResponse,
  NoContentResponse,
  OTPGenerateByEmailRequestBody,
  OTPGenerateByWhatsappRequestBody,
  OTPVerifyByLoggedInUserRequestBody,
  PhoneNumberVerficationResponse,
  PhoneVerificationChannelsRequest,
  Purpose,
  RequestOtpBody,
  User,
  VerificationByEmailResponse,
  VerifyOtpRequestBody,
} from '../../managers/sso';
import {
  accountKeys,
  apiKeyQueryOptions,
  getUserId,
  handleLoginError,
  handleSuccessfulLogin,
  useApiKey,
  useApiMutation,
  useRecaptchaManager,
  useSsoApiService,
  useUserId,
} from '../account';

import { describeError } from '../../utils/describeError';
/**
 * Forgot password is the only purpose that runs logged out, so it sends no
 * `Authorization` header. Both hooks must run, so only the resolver is picked.
 */
const useEmailOtpCredentials = (caller: string) => {
  const withUser = useSsoCredentials(caller);
  const anonymous = useAnonymousSsoCredentials(caller);

  return (purpose?: string): Promise<SsoCredentialsWithOptionalUser> =>
    purpose === Purpose.FORGOT_PASSWORD
      ? anonymous.resolve()
      : withUser.resolve();
};

export const useRequestEmailOtp = (caller: string) => {
  const api = useSsoApiService();
  const resolveCredentials = useEmailOtpCredentials(caller);
  return useApiMutation<
    GenerateOtpResponse,
    Error,
    { email: string; cultureCode: string; purpose: Purpose }
  >({
    mutationFn: async ({
      email,
      cultureCode,
      purpose,
    }: {
      email: string;
      cultureCode: string;
      purpose: Purpose;
    }) => {
      const { apiKey, clientId, accessToken } =
        await resolveCredentials(purpose);

      return api.requestEmailOtpForPurpose(clientId, apiKey, accessToken, {
        email: email,
        cultureCode: cultureCode,
        purpose: purpose,
      } as OTPGenerateByEmailRequestBody);
    },
    onError: async (error) => {
      console.error('requestEmailOtpForPurpose error:', describeError(error));
    },
  });
};

export const useRequestPhoneNumberOtp = (caller: string) => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const { resolve } = useSsoCredentials(caller);
  const recaptchaManager = useRecaptchaManager();
  return useApiMutation<
    GenerateOtpResponse,
    Error,
    { requestOtpBody: RequestOtpBody; otpPurpose?: Purpose }
  >({
    mutationFn: async ({
      requestOtpBody,
      otpPurpose = Purpose.USERNAME_CHANGE,
    }: {
      requestOtpBody: RequestOtpBody;
      otpPurpose?: Purpose;
    }) => {
      const { apiKey, clientId, accessToken } = await resolve();
      // Not useUserId: a mount-time call can fire before that query settles.
      const userId = await getUserId(queryClient);
      if (!userId) throw new Error('Missing user ID');

      return api.requestPhoneNumberOtp(
        userId,
        clientId,
        apiKey,
        accessToken,
        otpPurpose,
        requestOtpBody,
        await recaptchaManager
          .execute(RECAPTCHA_ACTIONS.USER_MOBILE_OTP)
          .catch(() => undefined),
      );
    },
    onError: async (error) => {
      console.error('requestPhoneNumberOtp error:', describeError(error));
    },
  });
};

export const useWhatsappOtpForPhoneNumberUpdate = (caller: string) => {
  const api = useSsoApiService();
  const { resolve } = useSsoCredentials(caller);
  return useApiMutation<
    GenerateOtpResponse,
    Error,
    {
      mobilePhone: string;
      cultureCode: string;
      platform: string;
      purpose: string;
    }
  >({
    mutationFn: async ({
      mobilePhone,
      cultureCode,
      platform,
      purpose,
    }: {
      mobilePhone: string;
      cultureCode: string;
      platform: string;
      purpose: string;
    }) => {
      const { apiKey, clientId, accessToken } = await resolve();

      return api.requestWhatsappOtpForPhoneNumberUpdate(
        clientId,
        apiKey,
        accessToken,
        {
          mobilePhone: mobilePhone,
          cultureCode: cultureCode,
          purpose: purpose,
          platform: platform,
        } as OTPGenerateByWhatsappRequestBody,
      );
    },
  });
};

export const usePhoneVerificationChannels = (caller: string) => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const { resolve } = useSsoCredentials(caller);

  return useApiMutation<
    PhoneNumberVerficationResponse,
    Error,
    PhoneVerificationChannelsRequest
  >({
    mutationFn: async (
      verificationRequiredRequestBody: PhoneVerificationChannelsRequest,
    ) => {
      const { apiKey, clientId, accessToken } = await resolve();

      const userId = await getUserId(queryClient);
      if (!userId) throw new Error('Missing user ID');

      return api.getPhoneVerificationChannels(
        userId,
        clientId,
        apiKey,
        accessToken,
        verificationRequiredRequestBody,
      );
    },
    onError: (error) => {
      console.error(
        'Error getting phone verification channels:',
        describeError(error),
      );
    },
  });
};

export const useVerifyEmailOtp = (caller: string) => {
  const api = useSsoApiService();
  const resolveCredentials = useEmailOtpCredentials(caller);
  const queryClient = useQueryClient();
  return useApiMutation<
    VerificationByEmailResponse,
    Error,
    VerifyOtpRequestBody
  >({
    mutationFn: async (verifyOtpBody: VerifyOtpRequestBody) => {
      const { apiKey, clientId, accessToken } = await resolveCredentials(
        verifyOtpBody.purpose,
      );

      return api.verifyEmailOtpForPurpose(
        clientId,
        apiKey,
        accessToken,
        verifyOtpBody,
      );
    },
    onSuccess: async (data) => {
      if (data.emailOtpAuthToken) {
        await otpTokenStore.setEmail(data.emailOtpAuthToken);
      }
      await queryClient.invalidateQueries({ queryKey: accountKeys.user });
    },
    onError: async (error) => {
      console.error('verifyEmailOtpForPurpose error:', describeError(error));
    },
  });
};

export const useVerifyPhoneNumberOtp = (caller: string) => {
  const api = useSsoApiService();
  const apikeyQuery = useApiKey();
  return useMutation({
    mutationFn: async ({
      verifyOtpBody,
      otpPurpose = Purpose.USERNAME_CHANGE,
    }: {
      verifyOtpBody: VerifyOtpRequestBody;
      otpPurpose?: Purpose;
    }) => {
      if (apikeyQuery.error) {
        console.error(
          'Error fetching API - ',
          caller,
          ':',
          describeError(apikeyQuery.error),
        );
      }

      const apiKey = await apikeyQuery.getToken();

      if (!apiKey?.apiKey) throw new Error('Missing API keys');
      if (!apiKey?.id) throw new Error('Missing Client ID');

      return api.verifyPhoneNumberOtp(
        apiKey.id,
        apiKey.apiKey || '',
        otpPurpose,
        verifyOtpBody,
      );
    },
    onSuccess: async (data) => {
      await otpTokenStore.setMobile(data.mobileOtpAuthToken);
    },
    onError: async (error) => {
      console.error('verifyPhoneNumberOtp error:', describeError(error));
    },
  });
};

export const useVerifyOTPByLoggedInUser = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const { resolve } = useSsoCredentials(caller);

  return useApiMutation<
    AccessTokenResponse,
    Error,
    OTPVerifyByLoggedInUserRequestBody
  >({
    mutationFn: async (
      verifyOtpRequestBody: OTPVerifyByLoggedInUserRequestBody,
    ) => {
      const { apiKey, clientId, accessToken } = await resolve();
      if (!userIdQuery.data) throw new Error('Missing user ID');

      return api.verifyOTPByLoggedInUser(
        userIdQuery.data,
        clientId,
        apiKey,
        accessToken,
        verifyOtpRequestBody,
      );
    },
    onError: (error) => {
      console.error(
        'Error verifying OTP by logged in user:',
        describeError(error),
      );
    },
  });
};

export const useVerifyWhatsappOTPByLoggedInUser = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const { resolve } = useSsoCredentials(caller);

  return useApiMutation<
    AccessTokenResponse,
    Error,
    { otp: string; verificationToken?: string }
  >({
    mutationFn: async ({ otp, verificationToken }) => {
      const { apiKey, clientId, accessToken } = await resolve();
      if (!userIdQuery.data) throw new Error('Missing user ID');

      return api.verifyWhatsappOTPByLoggedInUser(
        userIdQuery.data,
        clientId,
        apiKey,
        accessToken,
        verificationToken,
        { otp },
      );
    },
    onError: (error) => {
      console.error(
        'Error verifying WhatsApp OTP by logged in user:',
        describeError(error),
      );
    },
  });
};

export const useVerifyMobileLogin = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const recaptchaManager = useRecaptchaManager();

  return useMutation({
    mutationFn: async ({ otp }: { otp: string }) => {
      const clientKey = await queryClient.ensureQueryData(
        apiKeyQueryOptions(api),
      );

      if (!clientKey) throw new Error('Missing API keys');

      const verificationToken = await AsyncStorage.getItem(
        accountKeys.mobileVerificationToken[0],
      );

      if (!verificationToken) {
        throw new Error(
          'Verification token not found. Please request OTP again.',
        );
      }

      const recaptchaToken = await recaptchaManager.execute(
        RECAPTCHA_ACTIONS.LOGIN_MOBILE_PHONE_OTP,
      );

      return api.verifyMobileLogin(
        clientKey.apiKey || '',
        clientKey.id || '',
        verificationToken,
        recaptchaToken,
        { otp },
      );
    },
    onSuccess: async (data) => {
      await AsyncStorage.removeItem(accountKeys.mobileVerificationToken[0]);
      queryClient.setQueryData(accountKeys.mobileVerificationToken, null);
      await handleSuccessfulLogin(queryClient, data);
    },
    onError: async (error) => {
      await handleLoginError(queryClient, error, 'Mobile OTP Verification');
    },
  });
};

export const useSendLogin2FaCode = () => {
  const api = useSsoApiService();
  const apiKeyQuery = useApiKey();
  const recaptchaManager = useRecaptchaManager();

  return useMutation({
    mutationFn: async ({
      twoFAToken,
    }: {
      twoFAToken: string;
    }): Promise<string> => {
      const clientKey = await apiKeyQuery.getToken();
      if (!clientKey?.id || !clientKey?.apiKey) {
        throw new Error('Missing API keys');
      }

      const response = await api.sendTwoFaOtp(
        { method: TWO_FA_LOGIN_METHOD },
        clientKey.id,
        clientKey.apiKey,
        await recaptchaManager.execute(
          RECAPTCHA_ACTIONS.LOGIN_2FA_METHOD(TWO_FA_LOGIN_METHOD),
        ),
        twoFAToken,
      );

      return response?.twoFAToken ?? twoFAToken;
    },
  });
};

export const useVerifyLogin2Fa = () => {
  const api = useSsoApiService();
  const queryClient = useQueryClient();
  const apiKeyQuery = useApiKey();

  return useMutation({
    mutationFn: async ({
      otp,
      twoFAToken,
    }: {
      otp: string;
      twoFAToken: string;
    }): Promise<AccessTokenResponse> => {
      const clientKey = await apiKeyQuery.getToken();
      if (!clientKey?.id || !clientKey?.apiKey) {
        throw new Error('Missing API keys');
      }

      return api.verifyTwoFaOtp(
        { otp },
        clientKey.id,
        clientKey.apiKey,
        twoFAToken,
      );
    },

    onSuccess: async (data) => {
      await handleSuccessfulLogin(queryClient, data);
    },

    onError: async (error) => {
      await handleLoginError(queryClient, error, 'Credentials');
    },
  });
};

export const useChangePassword = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const { resolve } = useSsoCredentials(caller);
  const queryClient = useQueryClient();

  return useApiMutation<NoContentResponse, Error, ChangePasswordRequestBody>({
    mutationFn: async (
      changePasswordRequestBody: ChangePasswordRequestBody,
    ) => {
      const { apiKey, clientId, accessToken } = await resolve();
      if (!userIdQuery.data) throw new Error('Missing user ID');

      const emailOtpAuthToken = await otpTokenStore.getEmail();
      if (!emailOtpAuthToken) {
        throw new Error('Missing email OTP auth token');
      }

      return api.changePassword(
        userIdQuery.data,
        clientId,
        apiKey,
        accessToken,
        emailOtpAuthToken,
        changePasswordRequestBody,
      );
    },
    onSuccess: async () => {
      await otpTokenStore.removeEmail();
      await queryClient.invalidateQueries({ queryKey: accountKeys.user });
    },
    onError: async (error) => {
      console.error('changePassword error:', describeError(error));
    },
  });
};

/** Sets a new password when logged out: the email OTP token is the only auth. */
export const useResetPassword = (caller: string) => {
  const api = useSsoApiService();
  const { resolve } = useAnonymousSsoCredentials(caller);

  return useApiMutation<NoContentResponse, Error, ChangePasswordRequestBody>({
    mutationFn: async (
      changePasswordRequestBody: ChangePasswordRequestBody,
    ) => {
      const { apiKey, clientId } = await resolve();

      const emailOtpAuthToken = await otpTokenStore.getEmail();
      if (!emailOtpAuthToken) {
        throw new Error('Missing email OTP auth token');
      }

      return api.resetPasswordByEmailOtp(
        clientId,
        apiKey,
        emailOtpAuthToken,
        changePasswordRequestBody,
      );
    },
    onSuccess: async () => {
      await otpTokenStore.removeEmail();
    },
    onError: async (error) => {
      console.error('resetPasswordByEmailOtp error:', describeError(error));
    },
  });
};

export const useVerifyOtpAndUpdatePhoneNumber = (caller: string) => {
  const api = useSsoApiService();
  const userIdQuery = useUserId(caller);
  const { resolve } = useSsoCredentials(caller);
  const queryClient = useQueryClient();
  return useApiMutation<User, Error, { otp: string }>({
    mutationFn: async ({ otp }: { otp: string }) => {
      const { apiKey, clientId, accessToken } = await resolve();
      if (!userIdQuery.data) throw new Error('Missing user ID');

      const emailOtpAuthToken = await otpTokenStore.getEmail();
      if (!emailOtpAuthToken) {
        throw new Error('Missing email OTP auth token');
      }

      return api.verifyOtpAndUpdatePhoneNumber(
        userIdQuery.data,
        clientId,
        apiKey,
        accessToken,
        emailOtpAuthToken,
        {
          otp,
        },
      );
    },
    onSuccess: async () => {
      console.log('verifyOtpAndUpdatePhoneNumber Success response');
      await queryClient.invalidateQueries({ queryKey: accountKeys.user });
    },
    onError: async (error) => {
      console.error(
        'verifyOtpAndUpdatePhoneNumber error:',
        describeError(error),
      );
    },
  });
};
